import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Terms of Service"
        description="Terms of Service for NeuroNest — subscription, billing, acceptable use, AI Coach disclaimer, and your rights. Last updated May 2026."
        path="/terms"
      />
      <Navbar />
      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <article className="max-w-4xl mx-auto">
          <header className="mb-12">
            <h1
              data-testid="terms-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Terms of Service
            </h1>
            <p className="text-sm text-[#8B95A5]">
              Last updated: 3 May 2026
            </p>
          </header>

          <div className="text-sm text-[#5A6376] leading-relaxed space-y-4 mb-12">
            <p>
              These Terms of Service ("Terms") govern your use of the NeuroNest
              mobile application and related services (the "App") provided by{" "}
              <strong className="text-[#2F3A56]">NeuroNest Ltd</strong>{" "}
              ("NeuroNest", "we", "our", "us"). By creating an account or using
              the App, you agree to these Terms. If you do not agree, please do
              not use the App.
            </p>
            <p>
              If you use NeuroNest on behalf of a child or person in your care,
              the person creating the account accepts these Terms on their
              behalf.
            </p>
          </div>

          {/* 1 */}
          <Section title="1. About NeuroNest">
            <p className="mb-4">
              NeuroNest is a supportive mobile app designed for neurodivergent
              people — including those with autism, ADHD, sensory processing
              differences, and related neurotypes — and for the parents, carers
              and support workers who love them. The App provides sensory
              tracking, calming tools, routines, an AI Sensory Coach, and
              community-contributed sensory-friendly place listings.
            </p>
            <div className="bg-brand-peach/10 rounded-2xl p-5">
              <p className="text-[#2F3A56] font-semibold text-sm mb-2">
                NeuroNest is not a medical device.
              </p>
              <p className="text-xs text-[#5A6376] leading-relaxed">
                It is not intended to diagnose, treat, cure or prevent any
                health condition. The AI Sensory Coach provides general wellbeing
                support, not clinical advice. Always consult a qualified
                healthcare professional for medical concerns, and in a
                mental-health crisis call your local emergency services.
              </p>
            </div>
          </Section>

          {/* 2 */}
          <Section title="2. Accounts">
            <p className="mb-4">
              To use most of NeuroNest's features you must create an account.
              You agree to:
            </p>
            <ul className="space-y-2 mb-4">
              <Li>Provide accurate information when registering</Li>
              <Li>Keep your password confidential</Li>
              <Li>
                Be at least <strong>13 years old</strong> (or the minimum age of
                digital consent in your country)
              </Li>
              <Li>
                Notify us immediately if you suspect unauthorised access to your
                account
              </Li>
            </ul>
            <p className="mb-4">
              Children under 13 may only use NeuroNest's{" "}
              <strong>Child Mode</strong>, which must be set up and managed by a
              parent or carer on their own account (see our{" "}
              <a href="/privacy" className="text-brand-teal hover:underline">
                Privacy Policy
              </a>{" "}
              for more detail on child data).
            </p>
            <p>You are responsible for all activity that occurs under your account.</p>
          </Section>

          {/* 3 */}
          <Section title="3. Subscription, Billing and Free Trial">
            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.1 Free trial
            </h3>
            <p className="mb-4">
              New users receive a <strong>14-day free trial</strong> of
              NeuroNest Pro. You will not be charged during the trial. You may
              cancel at any time before the trial ends to avoid being charged.
            </p>

            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.2 Plans and pricing
            </h3>
            <p className="mb-3">
              After the trial, NeuroNest Pro is available as:
            </p>
            <ul className="space-y-2 mb-4">
              <Li>
                <strong>Monthly:</strong> £5.99 per month
              </Li>
              <Li>
                <strong>Yearly:</strong> £59.99 per year (approximately 17%
                saving)
              </Li>
            </ul>
            <p className="mb-4">
              Prices are displayed in the Google Play Store at the point of
              purchase and may vary by country based on Google Play's pricing.
            </p>

            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.3 Payment and auto-renewal
            </h3>
            <ul className="space-y-2 mb-4">
              <Li>
                Payment is charged to your <strong>Google Play account</strong>{" "}
                upon confirmation of purchase
              </Li>
              <Li>
                Subscriptions <strong>automatically renew</strong> at the end of
                each billing period (monthly or yearly) at the same price,
                unless you cancel at least 24 hours before the renewal date
              </Li>
              <Li>
                You can manage and cancel your subscription at any time in your
                Google Play account settings under{" "}
                <strong>Subscriptions &rarr; NeuroNest</strong>
              </Li>
              <Li>
                Cancelling stops future renewals. Your Pro access remains active
                until the end of the current billing period
              </Li>
            </ul>

            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.4 Refunds
            </h3>
            <p className="mb-4">
              Refunds are handled by Google Play according to their{" "}
              <a
                href="https://support.google.com/googleplay/answer/2479637"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-teal hover:underline"
              >
                refund policy
              </a>
              . We do not process refunds directly. If you have an exceptional
              concern, please email{" "}
              <strong>support@neuronest.global</strong> and we will do our best
              to help.
            </p>

            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.5 Free features
            </h3>
            <p className="mb-4">
              NeuroNest's <strong>Child Mode</strong> and a limited set of core
              calming tools remain <strong>free forever</strong>. We will give at
              least 30 days' notice before changing what is free.
            </p>

            <h3 className="font-heading text-base font-semibold text-[#2F3A56] mt-6 mb-3">
              3.6 Price changes
            </h3>
            <p>
              If we change the subscription price, we will notify you in advance
              via the App, email or Google Play. Your continued use after the
              change takes effect constitutes acceptance of the new price.
            </p>
          </Section>

          {/* 4 */}
          <Section title="4. Acceptable Use">
            <p className="mb-4">
              When using NeuroNest, you agree <strong>not to</strong>:
            </p>
            <ul className="space-y-2 mb-4">
              <Li>
                Misuse the AI Sensory Coach to generate harmful, hateful,
                illegal or sexually explicit content
              </Li>
              <Li>
                Submit false or defamatory listings to the Places directory
              </Li>
              <Li>
                Attempt to reverse-engineer, scrape, resell, or sublicense the
                App
              </Li>
              <Li>
                Share your account with others (each user needs their own
                account)
              </Li>
              <Li>
                Use the App in a way that breaches applicable laws or the rights
                of any third party
              </Li>
              <Li>
                Interfere with or disrupt the App's infrastructure, network or
                security
              </Li>
            </ul>
            <p>
              We may suspend or terminate accounts that breach these Terms,
              without refund where the breach is serious (e.g. deliberate
              misuse, fraud, or illegal activity).
            </p>
          </Section>

          {/* 5 */}
          <Section title="5. Your Content">
            <p className="mb-4">
              You retain ownership of any content you create in the App
              (check-ins, notes, voice messages, saved places).
            </p>
            <p className="mb-4">
              You grant NeuroNest a limited, non-exclusive, royalty-free licence
              to host, store and process your content <strong>solely</strong> to
              provide the App to you. We do not sell your content, train AI
              models on it, or share it with advertisers.
            </p>
            <p className="mb-4">
              Community-contributed content (such as public place reviews) may be
              visible to other users. Don't share anything you wouldn't want to
              be public.
            </p>
            <p>
              You can delete your content or your entire account at any time from{" "}
              <strong>Settings &rarr; Privacy</strong>. See our{" "}
              <a href="/privacy" className="text-brand-teal hover:underline">
                Privacy Policy
              </a>{" "}
              for what happens to your data after deletion.
            </p>
          </Section>

          {/* 6 */}
          <Section title="6. AI Sensory Coach Disclaimer">
            <p className="mb-4">
              The AI Sensory Coach is designed to support regulation, reflection
              and sensory awareness. It <strong>is not</strong>:
            </p>
            <ul className="space-y-2 mb-4">
              <Li>A licensed therapist, doctor, or mental-health professional</Li>
              <Li>A crisis service</Li>
              <Li>A replacement for professional care</Li>
            </ul>
            <div className="bg-brand-rose/10 rounded-2xl p-5 mb-4">
              <p className="text-[#2F3A56] font-semibold text-sm mb-2">
                In a mental-health emergency:
              </p>
              <p className="text-xs text-[#5A6376] leading-relaxed">
                Contact your local emergency services (999 in the UK, 911 in the
                US, 112 in the EU) or a recognised crisis line (Samaritans: 116
                123 in the UK).
              </p>
            </div>
            <p>
              Voice audio sent to the Coach is processed in-memory, never stored,
              and discarded immediately after transcription. See our{" "}
              <a href="/privacy" className="text-brand-teal hover:underline">
                Privacy Policy
              </a>{" "}
              for more.
            </p>
          </Section>

          {/* 7 */}
          <Section title="7. Privacy">
            <p>
              Your privacy matters deeply to us. Please read our{" "}
              <a href="/privacy" className="text-brand-teal hover:underline">
                Privacy Policy
              </a>{" "}
              to understand how we collect, use and protect your data. By using
              NeuroNest you also agree to the practices described in that policy.
            </p>
          </Section>

          {/* 8 */}
          <Section title="8. Changes to These Terms">
            <p>
              We may update these Terms to reflect changes in the App, applicable
              laws, or our business. When we do, we will update the "Last
              updated" date at the top and, for material changes, notify you via
              the App or email at least 7 days before the change takes effect.
              Your continued use of the App after the effective date means you
              accept the new Terms.
            </p>
          </Section>

          {/* 9 */}
          <Section title="9. Disclaimers and Limitation of Liability">
            <p className="mb-4">
              The App is provided <strong>"as is"</strong> and{" "}
              <strong>"as available"</strong> without warranties of any kind,
              whether express or implied, including fitness for a particular
              purpose, accuracy, or non-infringement.
            </p>
            <p className="mb-4">To the fullest extent permitted by law:</p>
            <ul className="space-y-2 mb-4">
              <Li>
                NeuroNest is not liable for any indirect, incidental, special or
                consequential damages
              </Li>
              <Li>
                Our total liability for any claim arising out of the App or these
                Terms is limited to the greater of: (a) the total amount you
                paid NeuroNest in the 12 months before the claim, or (b) £50
              </Li>
            </ul>
            <p>
              Nothing in these Terms limits liability that cannot be excluded
              under applicable law, including liability for death or personal
              injury caused by our negligence, or for fraud.
            </p>
          </Section>

          {/* 10 */}
          <Section title="10. Termination">
            <p>
              You can stop using the App and delete your account at any time. We
              may suspend or terminate your access if you breach these Terms, or
              if we need to stop providing the App for business, legal or
              technical reasons. We will give reasonable notice where possible.
            </p>
          </Section>

          {/* 11 */}
          <Section title="11. Governing Law">
            <p>
              These Terms are governed by the laws of{" "}
              <strong>England and Wales</strong>. Any dispute will be dealt with
              exclusively by the courts of England and Wales, except that if you
              are a consumer located elsewhere, you may bring proceedings in your
              country of residence.
            </p>
          </Section>

          {/* 12 */}
          <Section title="12. Contact Us">
            <p className="mb-3">
              For questions about these Terms, subscription issues, or anything
              else, please contact:
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@neuronest.global"
                className="text-brand-teal hover:underline"
              >
                support@neuronest.global
              </a>
              <br />
              Website:{" "}
              <a
                href="https://www.neuronest.global"
                className="text-brand-teal hover:underline"
              >
                www.neuronest.global
              </a>
            </p>
          </Section>

          <div className="mt-12 pt-8 border-t border-[#E7E9EC] text-center">
            <p className="text-xs text-[#8B95A5]">
              &copy; 2026 Universal Blocks Limited. All rights reserved.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-[#2F3A56] mb-4">
        {title}
      </h2>
      <div className="text-sm text-[#5A6376] leading-relaxed">{children}</div>
    </section>
  );
}

function Li({ children }) {
  return (
    <li className="flex items-start gap-2 text-sm text-[#5A6376] leading-relaxed">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}
